import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, BarChart3, BookOpen, Check, CheckCircle2, Compass, FileText, Footprints, Heart,
  Mic, Search, Sparkles, Upload, Wand2, Target, Award, ChevronDown, ChevronUp,
} from "lucide-react";
import { relativeTime } from "../../lib/relativeTime";

/**
 * Gradient palette used to color each phase consistently across the
 * dashboard: progress bar fill, segment chip, done-state icon background,
 * and any future per-phase chrome. Keep these in sync with LeftCards button
 * gradients (Phase 1 mustard, P2/P3 burnt-orange, P4 blue, P5 green).
 */
export const PHASE_GRADIENTS = {
  1: { from: "#FBBF24", to: "#CA8A04", solid: "#CA8A04", soft: "#FEF3C7" }, // mustard
  2: { from: "#F97316", to: "#B91C1C", solid: "#B91C1C", soft: "#FFE4E1" }, // burnt orange
  3: { from: "#20C1A7", to: "#0E5A4A", solid: "#0E5A4A", soft: "#CFFAFE" }, // teal — Network
  4: { from: "#3B82F6", to: "#1E40AF", solid: "#1E40AF", soft: "#DBEAFE" }, // blue
  5: { from: "#A855F7", to: "#7C3AED", solid: "#7C3AED", soft: "#EDE9FE" }, // purple — Stepping Stones
};

export function phaseGradient(id) {
  const g = PHASE_GRADIENTS[id];
  if (!g) return null;
  return `linear-gradient(135deg, ${g.from} 0%, ${g.to} 100%)`;
}

/* ========================================================== */
/*  Shared card chrome (used by all three left-side cards).   */
/* ========================================================== */
function ProCard({ children, testid }) {
  return (
    <div
      className="bg-white"
      style={{
        border: "1px solid #E2E8F0",
        borderRadius: 10,
        boxShadow: "0 1px 2px rgba(15,27,45,0.04), 0 1px 3px rgba(15,27,45,0.06)",
      }}
      data-testid={testid}
    >
      {children}
    </div>
  );
}

/**
 * Header for a phase card. When `collapsible` is true, the whole header acts
 * as a toggle button. When `done` is true, a green checkmark replaces the
 * accent icon to make completion obvious at a glance.
 */
function ProCardHeader({
  icon: Icon,
  eyebrow,
  title,
  accent = "#FF6A00",
  phaseId = null,
  collapsible = false,
  done = false,
  doneAt = null,
  open = true,
  onToggle,
  toggleTestid,
}) {
  const headerStyle = {
    borderBottom: collapsible && !open ? "none" : "1px solid #F1F5F9",
  };
  const rel = done ? relativeTime(doneAt) : "";
  const gradient = phaseId ? phaseGradient(phaseId) : null;
  const palette = phaseId ? PHASE_GRADIENTS[phaseId] : null;

  const inner = (
    <div className="flex items-start gap-3 px-5 pt-5 pb-3" style={headerStyle}>
      <div
        className="flex-shrink-0 flex items-center justify-center"
        style={{
          width: 36, height: 36,
          background: done && gradient ? gradient : "#F8FAFC",
          border: done && palette ? `1px solid ${palette.solid}` : "1px solid #E2E8F0",
          borderRadius: 8,
          color: done ? "#FFFFFF" : accent,
          boxShadow: done && palette ? `0 2px 6px ${palette.solid}40` : "none",
        }}
      >
        {done ? <CheckCircle2 size={18} strokeWidth={2.4} /> : <Icon size={18} strokeWidth={2} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-mono-rm uppercase text-[10px] tracking-widest"
             style={{ color: "#64748B", fontWeight: 600 }}>
          {eyebrow}
        </div>
        <div className="font-display text-[15px] leading-tight flex items-center gap-2 flex-wrap"
             style={{ color: "#0F1B2D", letterSpacing: "-0.01em", fontWeight: 600 }}>
          <span className="truncate">{title}</span>
          {done && (
            <span
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5"
              style={{
                background: palette ? palette.soft : "#ECFDF5",
                color: palette ? palette.solid : "#047857",
                border: `1px solid ${palette ? palette.solid : "#A7F3D0"}`,
                borderRadius: 4,
                fontWeight: 700,
                letterSpacing: "0.08em",
              }}
              data-testid="phase-done-pill"
            >
              <Check size={10} strokeWidth={3} /> Done{rel ? ` · ${rel}` : ""}
            </span>
          )}
        </div>
      </div>
      {collapsible && (
        <div className="flex-shrink-0 self-center" style={{ color: "#64748B" }}>
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      )}
    </div>
  );

  if (!collapsible) return inner;
  return (
    <button
      type="button"
      onClick={onToggle}
      className="w-full text-left transition-colors hover:bg-slate-50"
      style={{ borderRadius: 10, background: "transparent" }}
      data-testid={toggleTestid}
      aria-expanded={open}
    >
      {inner}
    </button>
  );
}

/**
 * Slide-down wrapper using the grid-template-rows: 0fr → 1fr trick so dynamic
 * content height animates smoothly without measuring DOM nodes. 200ms ease-out
 * matches the rest of the dashboard chrome.
 */
function CollapseBody({ open, children, testid }) {
  return (
    <div
      className="overflow-hidden"
      style={{
        display: "grid",
        gridTemplateRows: open ? "1fr" : "0fr",
        transition: "grid-template-rows 200ms ease-out",
      }}
      aria-hidden={!open}
      data-testid={testid}
    >
      <div style={{ minHeight: 0, overflow: "hidden" }}>{children}</div>
    </div>
  );
}

/**
 * Tiny hook so each phase card manages its own collapsed state. All phases
 * now start COLLAPSED by default — the user explicitly asked for a calmer
 * dashboard surface where every phase is opt-in to expand. The argument is
 * intentionally ignored (kept for call-site readability re: which phase
 * "logically" should be the next action) but no longer drives initial state.
 */
function usePhaseOpen(_defaultOpenIgnored) {
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen((v) => !v);
  return [open, toggle];
}

/* ============================================ */
/*  CARD 1 — Resume                              */
/* ============================================ */
export function ResumeCard({ hasResume, resumePreview, resumeFilename, resumeUpdatedAt, onUploadClick, hasAssessment }) {
  const navigate = useNavigate();
  const done = !!hasResume;
  const [open, toggle] = usePhaseOpen(!done);
  return (
    <ProCard testid="resume-card">
      <ProCardHeader
        icon={FileText}
        eyebrow="Phase 1 · Know Yourself"
        title={hasResume ? "Resume on file" : "Add your resume"}
        accent="#FF6A00"
        phaseId={1}
        collapsible
        done={done}
        doneAt={resumeUpdatedAt}
        open={open}
        onToggle={toggle}
        toggleTestid="resume-card-toggle"
      />
      <CollapseBody open={open} testid="resume-card-body">
      <div className="px-5 py-4">
        {hasResume ? (
          <>
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2 min-w-0">
                <Check size={13} style={{ color: "#20C1A7" }} />
                <span className="text-[12px] truncate" style={{ color: "#0F1B2D", fontWeight: 600 }}>
                  {resumeFilename || "your-resume"}
                </span>
              </div>
              <button
                type="button"
                onClick={onUploadClick}
                className="text-[11px] font-semibold flex items-center gap-1 px-2 py-1 transition-colors hover:bg-slate-50"
                style={{
                  color: "#0F1B2D",
                  border: "1px solid #CBD5E1",
                  borderRadius: 6,
                  background: "white",
                }}
                data-testid="resume-card-replace"
              >
                <Upload size={11} /> Replace
              </button>
            </div>
            <div
              className="p-3"
              style={{
                background: "#F8FAFC",
                border: "1px solid #E2E8F0",
                borderRadius: 6,
                maxHeight: 180,
                overflow: "hidden",
              }}
            >
              <pre
                className="text-[11px] whitespace-pre-wrap leading-relaxed overflow-hidden m-0"
                style={{
                  color: "#475569",
                  fontFamily: "'Poppins', system-ui, sans-serif",
                  fontWeight: 400,
                  maxHeight: 160,
                }}
              >
                {resumePreview}{resumePreview && resumePreview.length >= 600 ? "…" : ""}
              </pre>
            </div>
            {/* Skill assessment CTA — only fires once a resume is on file. */}
            <button
              type="button"
              onClick={() => navigate("/skills")}
              className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-2 text-[12px] font-semibold transition-transform hover:-translate-y-0.5"
              style={{
                background: hasAssessment
                  ? "linear-gradient(135deg, #FBBF24 0%, #CA8A04 100%)"
                  : "#0F1B2D",
                color: hasAssessment ? "#0F1B2D" : "white",
                border: hasAssessment ? "none" : "1px solid #0F1B2D",
                borderRadius: 8,
                boxShadow: hasAssessment
                  ? "0 4px 12px rgba(202,138,4,0.25)"
                  : "none",
              }}
              data-testid="resume-card-skill-assessment"
            >
              {hasAssessment ? (
                <><Check size={12} style={{ color: "#10B981" }} /> Skill Lab complete · re-run</>
              ) : (
                <><Sparkles size={12} /> Complete Skill Assessment →</>
              )}
            </button>
          </>
        ) : (
          <>
            <p className="text-[13px] leading-snug mb-3" style={{ color: "#475569" }}>
              Upload a PDF, DOCX, or TXT. Resmo parses it locally and never shares it.
            </p>
            <button
              type="button"
              onClick={onUploadClick}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-[13px] font-semibold transition-colors hover:bg-slate-900"
              style={{
                background: "#0F1B2D",
                color: "white",
                borderRadius: 8,
              }}
              data-testid="resume-card-upload"
            >
              <Upload size={14} /> Upload resume
            </button>
            <div className="font-mono-rm text-[10px] uppercase tracking-widest mt-2 text-center"
                 style={{ color: "#94A3B8" }}>
              PDF · DOCX · TXT
            </div>
            <div className="mt-3 pt-3 text-center" style={{ borderTop: "1px dashed #E2E8F0" }}>
              <div className="text-[12px] mb-1.5" style={{ color: "#475569" }}>
                Don't have a resume yet?
              </div>
              <Link
                to="/resume-builder"
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold transition-colors hover:underline rm-text-orange"
                data-testid="resume-card-build-from-scratch"
              >
                <Sparkles size={12} /> Build one from scratch →
              </Link>
            </div>
          </>
        )}
      </div>
      </CollapseBody>
    </ProCard>
  );
}

/* ============================================ */
/*  CARD 2 — Tools (career list)                 */
/* ============================================ */
export function ToolsCard({ hasResume }) {
  const navigate = useNavigate();
  const TOOLS = [
    { id: "skills", title: "Skills Lab",            desc: "Salary range + adjacent roles",          icon: BarChart3, route: "/skills" },
    { id: "role",   title: "Role Research",         desc: "Day-in-the-life · comp · ladders",       icon: Compass,   route: "/skills/role/new" },
    { id: "jobs",   title: "Job Search",            desc: "Live listings · one-click tailor",       icon: Search,    route: "/job-search" },
    { id: "mock",   title: "Interview Test Drive",  desc: "Practice with voice or text",            icon: Mic,       route: "/mock-interview" },
    { id: "tips",   title: "Research & Dev",        desc: "Tips, templates, and the live job market pulse", icon: BookOpen,  route: "/resources" },
    { id: "stones", title: "Stepping Stones",       desc: "Journal lessons from past interviews",   icon: Heart,     route: "/stepping-stones" },
  ];
  return (
    <ProCard testid="tools-card">
      <ProCardHeader
        icon={Sparkles}
        eyebrow="Step 02 · Explore"
        title="Career tools"
        accent="#20C1A7"
      />
      <div className="px-2 py-2">
        <ul className="divide-y" style={{ borderColor: "#F1F5F9" }}>
          {TOOLS.map((tool) => {
            const Icon = tool.icon;
            const locked = !hasResume;
            return (
              <li key={tool.id}>
                <button
                  type="button"
                  onClick={() => !locked && navigate(tool.route)}
                  disabled={locked}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors group"
                  style={{
                    cursor: locked ? "not-allowed" : "pointer",
                    opacity: locked ? 0.5 : 1,
                    borderRadius: 6,
                  }}
                  onMouseEnter={(e) => { if (!locked) e.currentTarget.style.background = "#F8FAFC"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                  data-testid={`tools-row-${tool.id}`}
                >
                  <div
                    className="flex-shrink-0 flex items-center justify-center"
                    style={{
                      width: 28, height: 28, borderRadius: 6,
                      background: "#F1F5F9", color: "#475569",
                    }}
                  >
                    <Icon size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] leading-tight truncate"
                         style={{ color: "#0F1B2D", fontWeight: 600 }}>
                      {tool.title}
                    </div>
                    <div className="text-[11px] leading-tight truncate"
                         style={{ color: "#64748B" }}>
                      {tool.desc}
                    </div>
                  </div>
                  <ArrowRight size={14}
                              className="opacity-0 group-hover:opacity-100 transition-opacity"
                              style={{ color: "#FF6A00" }} />
                </button>
              </li>
            );
          })}
        </ul>
        {!hasResume && (
          <div className="px-3 pt-2 pb-1 text-[11px]" style={{ color: "#94A3B8" }}>
            Upload your resume above to unlock these tools.
          </div>
        )}
      </div>
    </ProCard>
  );
}

/* ============================================ */
/*  CARD 3 — Tailor (paste a JD)                 */
/* ============================================ */
export function TailorCard({ hasResume, done = false, doneAt = null }) {
  const navigate = useNavigate();
  const [jdText, setJdText] = useState("");
  const [open, toggle] = usePhaseOpen(!done && hasResume);
  const submitJd = () => {
    const text = jdText.trim();
    if (!hasResume) { navigate("/feed-resume"); return; }
    if (!text) { navigate("/feed-job"); return; }
    navigate("/feed-job", { state: { prefill: { jobDescription: text, source: "dashboard" } } });
  };
  return (
    <ProCard testid="tailor-card">
      <ProCardHeader
        icon={Target}
        eyebrow="Phase 2a · Job Skill Matchmaker"
        title={done ? "Job Description tailored" : "Got a job description?"}
        accent="#FF6A00"
        phaseId={2}
        collapsible
        done={done}
        doneAt={doneAt}
        open={open}
        onToggle={toggle}
        toggleTestid="tailor-card-toggle"
      />
      <CollapseBody open={open} testid="tailor-card-body">
      <div className="px-5 py-4">
        <p className="text-[13px] leading-snug mb-3" style={{ color: "#475569" }}>
          {hasResume
            ? "Paste a Job Description — Resmo will score the fit, surface gaps, and tailor your resume."
            : "Add your resume first, then come back to paste a Job Description."}
        </p>
        {hasResume && (
          <textarea
            value={jdText}
            onChange={(e) => setJdText(e.target.value)}
            placeholder="Paste a Job Description here — title, requirements, anything from the listing…"
            rows={4}
            className="w-full text-[12px] p-3 mb-3 transition-colors focus:outline-none"
            style={{
              background: "#F8FAFC",
              color: "#0F1B2D",
              border: "1px solid #CBD5E1",
              fontFamily: "'Poppins', system-ui, sans-serif",
              resize: "vertical",
              borderRadius: 8,
            }}
            data-testid="tailor-textarea"
          />
        )}
        <button
          type="button"
          onClick={submitJd}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-[13px] font-semibold transition-transform hover:-translate-y-0.5"
          style={{
            background: "linear-gradient(135deg, #F97316 0%, #B91C1C 100%)",
            color: "white",
            borderRadius: 8,
            boxShadow: "0 4px 12px rgba(185,28,28,0.25)",
          }}
          data-testid="tailor-submit"
        >
          <Wand2 size={14} />
          {!hasResume ? "Add resume first" : jdText.trim() ? "Tailor my resume →" : "Open Tailor flow →"}
        </button>
      </div>
      </CollapseBody>
    </ProCard>
  );
}

/* ============================================ */
/*  CARD 4 — Resume Makeover (Phase 3)           */
/* ============================================ */
export function MakeoverCard({ makeover, studioStatus }) {
  const navigate = useNavigate();
  // Locked until they have an analysis to polish.
  const locked = !makeover;
  const totalTweaks = studioStatus?.suggestions_total || 0;
  const done = !!studioStatus?.has_draft;
  const [open, toggle] = usePhaseOpen(!done && !locked);
  return (
    <ProCard testid="makeover-card">
      <ProCardHeader
        icon={Wand2}
        eyebrow="Phase 2b · Resume Makeover"
        title={done ? "Studio draft saved" : locked ? "Tailor a Job Description first" : "Polish in Studio"}
        accent="#B91C1C"
        phaseId={2}
        collapsible
        done={done}
        doneAt={studioStatus?.draft_updated_at}
        open={open}
        onToggle={toggle}
        toggleTestid="makeover-card-toggle"
      />
      <CollapseBody open={open} testid="makeover-card-body">
      <div className="px-5 py-4">
        <p className="text-[13px] leading-snug mb-3" style={{ color: "#475569" }}>
          {locked
            ? "After Phase 2, the Studio shows you exactly which tweaks lift your ATS score."
            : "Open Studio for live ATS scoring, suggested edits, and one-click templates."}
        </p>
        <button
          type="button"
          onClick={() => navigate(makeover ? `/studio?analysis_id=${makeover.analysis_id}` : "/studio")}
          disabled={locked}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-[13px] font-semibold transition-transform hover:-translate-y-0.5"
          style={{
            background: locked ? "#E2E8F0" : "linear-gradient(135deg, #F97316 0%, #B91C1C 100%)",
            color: locked ? "#94A3B8" : "white",
            borderRadius: 8,
            cursor: locked ? "not-allowed" : "pointer",
            boxShadow: locked ? "none" : "0 4px 12px rgba(185,28,28,0.25)",
          }}
          data-testid="makeover-card-open-studio"
        >
          <Wand2 size={14} />
          {locked ? "Locked · need a Job Description first" : `Open Studio · ${totalTweaks} tweaks`}
        </button>
      </div>
      </CollapseBody>
    </ProCard>
  );
}

/* ============================================ */
/*  CARD 4.5 — Build Your Network (Phase 3)      */
/* ============================================ */
export function NetworkCard({ contactCount = 0, hasPitch = false }) {
  const navigate = useNavigate();
  const done = contactCount >= 3 || hasPitch;
  const [open, toggle] = usePhaseOpen(!done);
  return (
    <ProCard testid="network-card">
      <ProCardHeader
        icon={Footprints}
        eyebrow="Phase 3 · Build Your Network"
        title={done
          ? `${contactCount} contact${contactCount === 1 ? "" : "s"} in your network`
          : "Networking"}
        accent="#0E5A4A"
        phaseId={3}
        collapsible
        done={done}
        open={open}
        onToggle={toggle}
        toggleTestid="network-card-toggle"
      />
      <CollapseBody open={open} testid="network-card-body">
      <div className="px-5 py-4 space-y-2">
        <p className="text-[13px] leading-snug mb-1" style={{ color: "#475569" }}>
          {done
            ? "Keep nurturing your network — refresh sparks, log a new contact, or send a follow-up."
            : "Pitch · contacts · sparks · mindset · events. Resmo's full networking arsenal in one place."}
        </p>
        <button
          type="button"
          onClick={() => navigate("/network")}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-[13px] font-semibold transition-transform hover:-translate-y-0.5"
          style={{
            background: "linear-gradient(135deg, #20C1A7 0%, #0E5A4A 100%)",
            color: "white",
            border: "none",
            borderRadius: 8,
            boxShadow: "0 4px 12px rgba(14,90,74,0.25)",
          }}
          data-testid="network-card-open"
        >
          <Footprints size={13} /> Open Network →
        </button>
      </div>
      </CollapseBody>
    </ProCard>
  );
}

/* ============================================ */
/*  CARD 5 — Interview Test Drive & Prep (Phase 4) */
/* ============================================ */
export function InterviewCard({ makeover, recentPlaybooks }) {
  const navigate = useNavigate();
  const locked = !makeover;
  const playbooks = recentPlaybooks || [];
  const done = !locked && playbooks.length > 0;
  const doneAt = done ? (playbooks[0]?.created_at || null) : null;
  const [open, toggle] = usePhaseOpen(!done && !locked);
  return (
    <ProCard testid="interview-card">
      <ProCardHeader
        icon={Mic}
        eyebrow="Phase 4 · Interview Test Drive & Prep"
        title={done ? "Playbook ready" : locked ? "Tailor first, prep next" : "Get ready for the interview"}
        accent="#FFC145"
        phaseId={4}
        collapsible
        done={done}
        doneAt={doneAt}
        open={open}
        onToggle={toggle}
        toggleTestid="interview-card-toggle"
      />
      <CollapseBody open={open} testid="interview-card-body">
      <div className="px-5 py-4 space-y-2">
        <p className="text-[13px] leading-snug mb-1" style={{ color: "#475569" }}>
          {locked
            ? "Once your resume is tailored, this card unlocks playbooks + practice runs."
            : "Build your interview playbook and run a live mock with Resmo."}
        </p>
        <button
          type="button"
          onClick={() => navigate(makeover ? `/playbooks?analysis_id=${makeover.analysis_id}` : "/playbooks")}
          disabled={locked}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 text-[12px] font-semibold transition-colors"
          style={{
            background: locked ? "#E2E8F0" : "white",
            color: locked ? "#94A3B8" : "#0F1B2D",
            border: "1px solid " + (locked ? "#E2E8F0" : "#0F1B2D"),
            borderRadius: 8,
            cursor: locked ? "not-allowed" : "pointer",
          }}
          data-testid="interview-card-playbook"
        >
          <BookOpen size={12} /> Build interview playbook
        </button>
        <button
          type="button"
          onClick={() => navigate(makeover ? `/mock-interview?analysis_id=${makeover.analysis_id}` : "/mock-interview")}
          disabled={locked}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-[13px] font-semibold transition-transform hover:-translate-y-0.5"
          style={{
            background: locked ? "#E2E8F0" : "linear-gradient(135deg, #3B82F6 0%, #1E40AF 100%)",
            color: locked ? "#94A3B8" : "white",
            border: "none",
            borderRadius: 8,
            cursor: locked ? "not-allowed" : "pointer",
            boxShadow: locked ? "none" : "0 4px 12px rgba(30,64,175,0.25)",
          }}
          data-testid="interview-card-mock"
        >
          <Mic size={13} /> Run a mock interview
        </button>
      </div>
      </CollapseBody>
    </ProCard>
  );
}

/* ============================================ */
/*  CARD 6 — Stepping Stones (Phase 5)           */
/* ============================================ */
export function StonesCard({ recentStones }) {
  const navigate = useNavigate();
  const stones = recentStones || [];
  const hasStones = stones.length > 0;
  const done = hasStones;
  const doneAt = done ? (stones[0]?.created_at || null) : null;
  const [open, toggle] = usePhaseOpen(!done);
  return (
    <ProCard testid="stones-card">
      <ProCardHeader
        icon={Heart}
        eyebrow="Phase 5 · Stepping Stones"
        title={done ? "Lesson logged" : "After the interview"}
        accent="#FF6A00"
        phaseId={5}
        collapsible
        done={done}
        doneAt={doneAt}
        open={open}
        onToggle={toggle}
        toggleTestid="stones-card-toggle"
      />
      <CollapseBody open={open} testid="stones-card-body">
      <div className="px-5 py-4">
        <p className="text-[13px] leading-snug mb-3" style={{ color: "#475569" }}>
          {hasStones
            ? "Log what worked, what didn't, and how you'll lift the next interview."
            : "Whether you got the job or not — capture the lessons. Future-you will thank you."}
        </p>
        <button
          type="button"
          onClick={() => navigate("/stepping-stones?new=1")}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-[13px] font-semibold transition-transform hover:-translate-y-0.5"
          style={{
            background: "linear-gradient(135deg, #10B981 0%, #047857 100%)",
            color: "white",
            border: "none",
            borderRadius: 8,
            boxShadow: "0 4px 12px rgba(4,120,87,0.25)",
          }}
          data-testid="stones-card-new"
        >
          <Award size={13} /> Log a stepping stone →
        </button>
      </div>
      </CollapseBody>
    </ProCard>
  );
}

/* ============================================ */
/*  CAREER TOOLS — bottom collapsible tray       */
/* ============================================ */
export function CareerToolsCollapsible({ hasResume, defaultOpen = false }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(defaultOpen);
  const TOOLS = [
    { id: "skills", title: "Skills Lab",            desc: "Salary range + adjacent roles",          icon: BarChart3, route: "/skills" },
    { id: "role",   title: "Role Research",         desc: "Day-in-the-life · comp · ladders",       icon: Compass,   route: "/skills/role/new" },
    { id: "jobs",   title: "Job Search",            desc: "Live listings · one-click tailor",       icon: Search,    route: "/job-search" },
    { id: "mock",   title: "Interview Test Drive",  desc: "Practice with voice or text",            icon: Mic,       route: "/mock-interview" },
    { id: "tips",   title: "Research & Dev",        desc: "Tips, templates, market pulse",          icon: BookOpen,  route: "/resources" },
    { id: "stones", title: "Stepping Stones",       desc: "Journal lessons from past interviews",   icon: Heart,     route: "/stepping-stones" },
  ];
  return (
    <ProCard testid="career-tools-collapsible">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-5 py-3"
        style={{ borderBottom: open ? "1px solid #F1F5F9" : "none" }}
        data-testid="career-tools-toggle"
      >
        <Sparkles size={14} style={{ color: "#20C1A7" }} />
        <div className="flex-1 text-left">
          <div className="font-mono-rm uppercase text-[10px] tracking-widest"
               style={{ color: "#64748B", fontWeight: 600 }}>
            Other tools
          </div>
          <div className="font-display text-base leading-tight"
               style={{ color: "#0F1B2D", letterSpacing: "-0.01em", fontWeight: 600 }}>
            Career toolbox
          </div>
        </div>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <div className="px-2 py-2">
          <ul className="divide-y" style={{ borderColor: "#F1F5F9" }}>
            {TOOLS.map((tool) => {
              const Icon = tool.icon;
              const locked = !hasResume;
              return (
                <li key={tool.id}>
                  <button
                    type="button"
                    onClick={() => !locked && navigate(tool.route)}
                    disabled={locked}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors"
                    style={{ cursor: locked ? "not-allowed" : "pointer", opacity: locked ? 0.5 : 1 }}
                    data-testid={`career-tool-${tool.id}`}
                  >
                    <Icon size={14} className="rm-text-orange flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] font-semibold" style={{ color: "#0F1B2D" }}>
                        {tool.title}
                      </div>
                      <div className="text-[11px]" style={{ color: "#64748B" }}>
                        {tool.desc}
                      </div>
                    </div>
                    {!locked && <ArrowRight size={11} className="opacity-50" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </ProCard>
  );
}

